/**
 * Clock offset utility for correcting system clock drift.
 *
 * When a device's system clock is off (even by a few minutes), the player's
 * live pipeline breaks because it requests data for wrong time ranges.
 * This module measures the offset between the local clock and the server clock,
 * then provides a correctedNow() function that compensates for the drift.
 */

import { OneMinuteInMillis, OneSecondInMillis } from '@racemap/utilities/consts/time';

let clockOffset = 0;
let acceptedProbeStart = -1;
const CLOCK_OFFSET_WARN_THRESHOLD_MS = 30_000; // 30 seconds
const RECALIBRATE_INTERVAL = OneMinuteInMillis;
const MAX_PROBE_RTT = 5 * OneSecondInMillis;

// A cached response replays a stale Date header with a near-zero RTT (RFC 9111 5.1) and Age is not
// CORS-safelisted, so it cannot be corrected for. This route is no-store end to end and DB-free.
const TIME_PROBE_URL = '/api/version';

/**
 * Calibrate the local clock against the server by measuring the offset
 * from the HTTP Date response header.
 *
 * Uses a simple NTP-like approach: measures round-trip time (RTT) and
 * estimates that the server timestamp corresponds to the midpoint of the request.
 *
 * Safe to call repeatedly: a failed, slow or outdated measurement keeps the previous offset.
 */
export async function calibrateClock(): Promise<void> {
  try {
    // Monotonic clock: an OS clock step while the request is in flight would otherwise count as
    // round-trip time, baking half of the step into the offset.
    const before = performance.now();
    const res = await fetch(TIME_PROBE_URL, { cache: 'no-store' });
    const rtt = performance.now() - before;
    const localTime = Date.now();

    // The midpoint estimate below is only worth ±rtt/2, so a probe that crawled — or hung across
    // standby — implies an offset too coarse to install. The next tick measures again.
    if (rtt > MAX_PROBE_RTT) return;

    const dateHeader = res.headers.get('Date');
    if (dateHeader == null) return;

    const serverTime = new Date(dateHeader).getTime();
    if (Number.isNaN(serverTime)) return;

    // A probe that started earlier may still answer last — e.g. one left in flight over standby,
    // resolving after the resumed tab already measured the NTP-corrected clock. The monotonic clock
    // can stall across suspend, so an equal start is not proof of an equally fresh probe.
    if (before <= acceptedProbeStart) return;
    acceptedProbeStart = before;

    clockOffset = serverTime + rtt / 2 - localTime;

    if (Math.abs(clockOffset) > CLOCK_OFFSET_WARN_THRESHOLD_MS) {
      console.warn(
        `[clock] Device clock is off by ${Math.round(clockOffset / 1000)}s from server time.`,
      );
    }
  } catch (err) {
    console.warn('[clock] Failed to calibrate clock:', err);
  }
}

/**
 * Calibrates now and keeps the offset fresh for the lifetime of the tab.
 *
 * A device waking from standby resyncs via NTP after page load. A stale boot offset makes every
 * correctedNow() caller request the wrong time window, freezing the live pipeline until reload.
 */
export async function initClock(): Promise<void> {
  await calibrateClock();
  setInterval(() => void calibrateClock(), RECALIBRATE_INTERVAL);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void calibrateClock();
  });
}

/**
 * Returns the current time corrected for the measured clock offset.
 * If calibration hasn't been performed or failed, returns Date.now() as-is.
 */
export function correctedNow(): number {
  return Date.now() + clockOffset;
}

/**
 * Resets the clock offset (mainly useful for testing).
 */
export function resetClockOffset(): void {
  clockOffset = 0;
  acceptedProbeStart = -1;
}
